import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "../auth";
import { readCache, writeCache } from "../dataCache";
import MobileScrollArea from "./MobileScrollArea";

// Admin-only activity log. Shows recent events (sent / open / click /
// unsubscribe / bounce) across EVERY user, with the owning user resolved so
// the admin can see whose activity it is. Protected server-side by requireAdmin.
//
// All logs are fetched once and filtered client-side (type / user / date /
// search) so switching filters is instant with no reload flash.
const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "sent", label: "Sent" },
  { value: "open", label: "Open" },
  { value: "click", label: "Click" },
  { value: "unsubscribe", label: "Unsubscribe" },
  { value: "bounce", label: "Bounce" }
];

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "all", label: "All" },
  { value: "custom", label: "Custom" }
];

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (err) {
    return String(iso);
  }
}

// Local YYYY-MM-DD for a date value (used for the date-range inputs & compare).
function toDateInput(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Turn a named range preset into concrete from/to YYYY-MM-DD values.
function rangeFor(preset) {
  const now = new Date();
  if (preset === "today") return { from: toDateInput(now), to: toDateInput(now) };
  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return { from: toDateInput(y), to: toDateInput(y) };
  }
  if (preset === "this_month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toDateInput(start), to: toDateInput(now) };
  }
  if (preset === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toDateInput(start), to: toDateInput(end) };
  }
  if (preset === "all") return { from: "", to: "" };
  return null; // custom -> leave the inputs as-is
}

// Page-size options that grow with the dataset: the more data an admin has,
// the larger the per-page limits they can choose.
function pageSizeOptions(total) {
  const out = [10, 25, 50, 100];
  const tiers = [250, 500, 1000, 2500, 5000, 10000];
  for (const t of tiers) {
    if (total > out[out.length - 1]) out.push(t);
    else break;
  }
  return out;
}

export default function AdminLogs() {
  const today = toDateInput(new Date());

  const [logs, setLogs] = useState(() => readCache("admin_logs", []));
  const [users, setUsers] = useState(() => readCache("admin_logs_users", []));
  const [loading, setLoading] = useState(() => readCache("admin_logs", null) === null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [type, setType] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rangePreset, setRangePreset] = useState("today");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  const [perPage, setPerPage] = useState(25);
  const [pageNo, setPageNo] = useState(1);

  const load = useCallback(async () => {
    if (readCache("admin_logs", null) === null) setLoading(true);
    setError("");
    try {
      const [logRes, userRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/logs`),
        fetch(`${API_URL}/api/admin/users`)
      ]);
      if (logRes.status === 403 || userRes.status === 403) {
        throw new Error("You do not have permission to view logs.");
      }
      const logData = await logRes.json().catch(() => ({}));
      const userData = await userRes.json().catch(() => ({}));
      if (!logRes.ok) throw new Error(logData.message || "Failed to load logs.");
      const nextLogs = Array.isArray(logData.logs) ? logData.logs : [];
      const nextUsers = Array.isArray(userData.users) ? userData.users : [];
      setLogs(nextLogs);
      setUsers(nextUsers);
      writeCache("admin_logs", nextLogs);
      writeCache("admin_logs_users", nextUsers);
    } catch (err) {
      setError(err.message || "Failed to load logs.");
    } finally {
      setLoading(false);
    }
  }, []);

  // REQ-32: manual Refresh always shows visible feedback (loading dots) and
  // stays visible for a minimum of 3 seconds so the refresh is clearly felt,
  // even when cached data returns instantly.
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    const startedAt = Date.now();
    try {
      await load();
    } finally {
      const elapsed = Date.now() - startedAt;
      if (elapsed < 3000) await new Promise((r) => setTimeout(r, 3000 - elapsed));
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  // Applying a named range preset updates the two date inputs.
  function applyPreset(preset) {
    setRangePreset(preset);
    const r = rangeFor(preset);
    if (r) {
      setDateFrom(r.from);
      setDateTo(r.to);
    }
  }

  const term = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (type && String(l.type || "") !== type) return false;
      if (userFilter && String(l.ownerEmail || "") !== userFilter) return false;

      if (dateFrom || dateTo) {
        const d = toDateInput(l.createdAt);
        if (!d) return false;
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
      }

      if (term) {
        const hay = `${l.email || ""} ${l.ownerEmail || ""} ${l.ownerName || ""} ${l.type || ""} ${l.url || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [logs, type, userFilter, dateFrom, dateTo, term]);

  // Reset to first page whenever the filters or page size change.
  useEffect(() => {
    setPageNo(1);
  }, [type, userFilter, dateFrom, dateTo, term, perPage]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(pageNo, totalPages);
  const startIdx = (currentPage - 1) * perPage;
  const pageRows = filtered.slice(startIdx, startIdx + perPage);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + perPage, total);
  const sizeOpts = pageSizeOptions(total);

  return (
    <div className="sx-admin">
      <div className="sx-admin-head">
        <div>
          <h2 className="sx-admin-title">Activity Logs</h2>
          <p className="sx-admin-sub">Every send, open, click and unsubscribe across all users.</p>
        </div>
        <div className="sx-logs-head-right">
          <div className="sx-logs-daterange">
            <label>
              <span>Range</span>
              <select
                value={rangePreset}
                onChange={(e) => applyPreset(e.target.value)}
              >
                {RANGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>From</span>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => { setDateFrom(e.target.value); setRangePreset("custom"); }}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => { setDateTo(e.target.value); setRangePreset("custom"); }}
              />
            </label>
          </div>
          <button className="sx-admin-refresh" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? (
              <>Refreshing<span className="sx-refresh-dots"><i></i><i></i><i></i></span></>
            ) : "Refresh"}
          </button>
        </div>
      </div>

      {error ? <div className="sx-admin-error">{error}</div> : null}

      <div className="sx-logs-mobile-filters">
        <div className="sx-logs-mobile-date-block">
          <div className="sx-logs-mobile-label">Date range</div>
          <div className="sx-logs-mobile-date-row">
            <label>
              <span>From</span>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => { setDateFrom(e.target.value); setRangePreset("custom"); }}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => { setDateTo(e.target.value); setRangePreset("custom"); }}
              />
            </label>
          </div>
        </div>

        <div className="sx-logs-mobile-select-row">
          <select
            aria-label="Date range preset"
            value={rangePreset}
            onChange={(e) => applyPreset(e.target.value)}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            aria-label="Activity type"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value || "all"} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <select
          className="sx-logs-mobile-users"
          aria-label="User"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        >
          <option value="">All users</option>
          {users.map((u) => (
            <option key={u.id || u._id || u.email} value={u.email}>
              {u.name ? `${u.name} (${u.email})` : u.email}
            </option>
          ))}
        </select>

        <div className="sx-logs-mobile-search-row">
          <input
            className="sx-admin-search"
            placeholder="Search by email, user or link…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="sx-admin-count">{total} event{total === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div className="sx-admin-toolbar sx-logs-toolbar">
        <input
          className="sx-admin-search"
          placeholder="Search by email, user or link…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="sx-admin-count">{total} event{total === 1 ? "" : "s"}</span>

        <div className="sx-logs-dropdowns">
          <select
            className="sx-logs-select"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
          >
            <option value="">All users</option>
            {users.map((u) => (
              <option key={u.id || u._id || u.email} value={u.email}>
                {u.name ? `${u.name} (${u.email})` : u.email}
              </option>
            ))}
          </select>

          <select
            className="sx-logs-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value || "all"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <MobileScrollArea className="sx-admin-table-wrap" ariaLabel="activity logs table">
        <table className="sx-admin-table sx-logs-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Type</th>
              <th>Recipient</th>
              <th>Details</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {!loading && total === 0 ? (
              <tr><td colSpan={5} className="sx-admin-empty">No activity found for the selected filters.</td></tr>
            ) : null}
            {pageRows.map((l) => (
              <tr key={l.id}>
                <td>
                  <div className="sx-admin-user-name">{l.ownerName || "—"}</div>
                  <div className="sx-admin-user-email">{l.ownerEmail}</div>
                </td>
                <td><span className={`sx-badge sx-log-${l.type}`}>{l.type}</span></td>
                <td>{l.email || "—"}</td>
                <td className="sx-admin-muted">{l.url || l.ip || "—"}</td>
                <td className="sx-admin-muted">{fmtTime(l.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </MobileScrollArea>

      <div className="sx-logs-pagination">
        <div className="sx-logs-perpage">
          <span>Show</span>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
          >
            {sizeOpts.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span>per page</span>
        </div>

        <div className="sx-logs-pageinfo">
          Showing {showingFrom} to {showingTo} of {total} results
        </div>

        <div className="sx-logs-pagebtns">
          <button
            className="sx-admin-btn"
            disabled={currentPage <= 1}
            onClick={() => setPageNo((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="sx-logs-pagenum">Page {currentPage} of {totalPages}</span>
          <button
            className="sx-admin-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setPageNo((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
